import {NetworkEnums} from '../../../../shared/GameEnums'
import {CA} from '../analytics/Analytics'
import {UIGameSpriteHandler} from '../UIGameSpriteHandler'
import {AnalyticEventCategory, AnalyticEventType, JoinRoomMethod} from '../../../../shared/entity_datas/EntityDatas'
import {socket} from '../EntryShared'
import {
    ChannelAction,
    ChannelJoinData,
    ChannelJoinRoomOrGameData,
    LobbyAction,
    PRIMARY_CHANNEL_ACTIONS,
    PRIMARY_LOBBY_ACTIONS,
    RoomType, SendChannelActionData,
    SendLobbyActionData,
} from '@colonist/lobby-types'
import {BotDifficulty, GameSpeedSettings, ModeSettingEnum} from '@colonist/game-base-types'
import {AbortError, BigId, ClientMessageSubType, COLONIST_VERSION} from '@colonist/types'
import {DiscordServerProfile} from '../../../../shared/DiscordActivityTypes'
import {BeginnerModeLevel, getBotsForBeginnerModeLevel, getMapForBeginnerModeLevel} from '@colonist/game-beginner-mode'
import {StorageWrapper} from '../StorageWrapper'
import {GeneralAction, InviteFriendToRoomData, RespondToFriendRoomInviteData, SendGeneralActionData, OnlineStatus} from '../../../../shared/friends/FriendTypes'
import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {UIUtilsFriends} from '../ui_friends/UIUtilsFriends'
import {UIUtilsNotifications} from '../notifications/UIUtilsNotifications'
import {GetNotificationsData} from '../../../../shared/NotificationTypes'
import {ClientUserState} from '../ClientUserState.ts'

export class SocketLobbySend {
    static interactedWithSite() {
        this.sendLobby(LobbyAction.InteractedWithSite, true)
    }

    static setAdBlockStatusToUserSession(adsBlocked: boolean) {
        this.sendLobby(LobbyAction.SetAdBlockStatus, adsBlocked)
    }

    static async continueCampaignMode() {
        await this.waitUntilGameCanBeStarted()

        const level = StorageWrapper.getBeginnerModeLevel()
        if(level == BeginnerModeLevel.End) return this.start1v1BeginnerBotGame()
        this.startBeginnerMode(level)
    }

    static async clickedStartHardBotGame() {
        await this.waitUntilGameCanBeStarted()

        const data: NetworkEnums.StartBotGameData = {
            clientVersion: COLONIST_VERSION,
            bots: [
                BotDifficulty.BotMedium,
                BotDifficulty.BotMedium,
                BotDifficulty.BotMedium,
            ],
        }
        this.sendLobby(LobbyAction.ClickedBotGame, data)
    }

    static async clickedStartMediumBotGame() {
        await this.waitUntilGameCanBeStarted()

        const data: NetworkEnums.StartBotGameData = {
            clientVersion: COLONIST_VERSION,
            bots: [
                BotDifficulty.BotMedium,
                BotDifficulty.BotEasy,
                BotDifficulty.BotEasy,
            ],
        }
        this.sendLobby(LobbyAction.ClickedBotGame, data)
    }

    static async clickedStartEasyBotGame() {
        await this.waitUntilGameCanBeStarted()

        const data: NetworkEnums.StartBotGameData = {
            clientVersion: COLONIST_VERSION,
            bots: [
                BotDifficulty.BotEasy,
                BotDifficulty.BotEasy,
                BotDifficulty.BotEasy,
            ],
        }
        this.sendLobby(LobbyAction.ClickedBotGame, data)
    }

    static async startShuffleBotGame() {
        await this.waitUntilGameCanBeStarted()

        const data: NetworkEnums.StartShuffleBotGameData = {
            clientVersion: COLONIST_VERSION,
        }
        this.sendLobby(LobbyAction.ClickedStartShuffleBotGame, data)
    }

    static async clickedStartColonistRushBotGame() {
        await this.waitUntilGameCanBeStarted()

        const data: NetworkEnums.StartBotGameData = {
            clientVersion: COLONIST_VERSION,
            bots: [
                BotDifficulty.BotMedium,
                BotDifficulty.BotMedium,
                BotDifficulty.BotMedium,
            ],
            victoryPointsToWin: 10,
            modeSetting: ModeSettingEnum.ColonistRush4P,
            gameSpeed: GameSpeedSettings.Rush15s,
            cardDiscardLimit: 9,
        }
        this.sendLobby(LobbyAction.ClickedBotGame, data)
    }

    static registerToFriendService() {
        if(!UIUtilsFriends.canUseOnlineFriendService()) return
        this.sendGeneral(GeneralAction.RegisterToFriendService, {})
    }

    static changedOnlineStatus(newOnlineStatus: OnlineStatus) {
        if(!UIUtilsFriends.canUseOnlineFriendService()) return
        SocketLobbySend.sendGeneral(GeneralAction.ChangeOnlineStatus, newOnlineStatus)
    }

    static getAllFriendsOnlineStatus() {
        if(!UIUtilsFriends.canUseOnlineFriendService()) return
        SocketLobbySend.sendGeneral(GeneralAction.GetAllFriendsOnlineStatus, {})
    }

    static inviteFriendToRoom(receiverUserId: BigId, roomId: string) {
        if(!UIUtilsFriends.canUseOnlineFriendService()) return
        const data: InviteFriendToRoomData = {
            receiverUserId: receiverUserId,
            roomId: roomId,
        }
        this.sendGeneral(GeneralAction.InviteFriendToRoom, data)
    }

    static respondToFriendRoomInvite(roomId: string, accept: boolean, senderUserId: BigId) {
        if(!UIUtilsFriends.canUseOnlineFriendService()) return
        const data: RespondToFriendRoomInviteData = {
            roomId: roomId,
            accept: accept,
            senderUserId: senderUserId,
        }
        this.sendGeneral(GeneralAction.RespondToFriendRoomInvite, data)
    }

    static getAllFriendRoomInvitesReceived() {
        if(!UIUtilsFriends.canUseOnlineFriendService()) return
        this.sendGeneral(GeneralAction.GetAllRoomInvitesReceived, {})
    }

    static registerToNotificationService() {
        if(!UIUtilsNotifications.canUseLiveNotificationService()) return
        this.sendGeneral(GeneralAction.RegisterToNotificationService, {})
    }

    static markAllNotificationsAsRead() {
        if(!UIUtilsNotifications.canUseLiveNotificationService()) return
        this.sendGeneral(GeneralAction.MarkAllNotificationsAsRead, {})
    }

    static markNotificationAsRead(notificationId: BigId) {
        if(!UIUtilsNotifications.canUseLiveNotificationService()) return
        this.sendGeneral(GeneralAction.MarkNotificationAsRead, notificationId)
    }

    static getNotifications(getNotificationData: GetNotificationsData) {
        if(!UIUtilsNotifications.canUseLiveNotificationService()) return
        this.sendGeneral(GeneralAction.GetNotifications, getNotificationData)
    }

    static watchRoomList() {
        this.sendLobby(LobbyAction.WatchRoomList, {})
    }

    static stopWatchingRoomList() {
        this.sendLobby(LobbyAction.StopWatchingRoomList, {})
    }

    static clickedCreateRoom(roomType: RoomType) {
        this.sendAnalyticEvent(AnalyticEventType.JoinedRoom, AnalyticEventCategory.Room, undefined, JoinRoomMethod.ClickedCreateRoom)
        CA.sendGoogleAnalyticsEvent(CA.Category.Lobby, CA.Label.ClickedCreateRoom)
        const data: NetworkEnums.ClickedCreateRoomData = {
            roomType: roomType,
            clientVersion: COLONIST_VERSION,
        }
        this.sendLobby(LobbyAction.ClickedCreateRoom, data)
    }

    static async clickedJoinRoom(roomId: string) {
        await this.waitUntilGameCanBeStarted()

        this.sendAnalyticEvent(AnalyticEventType.JoinedRoom, AnalyticEventCategory.Room, roomId, JoinRoomMethod.ClickedLobbyRoom)
        CA.sendGoogleAnalyticsEvent(CA.Category.Lobby, CA.Label.ClickedJoinRoom)
        const data: NetworkEnums.AccessGameLinkData = {
            gameId: roomId,
            clientVersion: COLONIST_VERSION,
        }
        this.sendLobby(LobbyAction.ClickedJoinRoom, data)
    }

    static async clickedSpectateGame(gameId: string) {
        await this.waitUntilGameCanBeStarted()

        CA.sendGoogleAnalyticsEvent(CA.Category.Lobby, CA.Label.ClickedSpectate)
        const data: NetworkEnums.AccessGameLinkData = {
            gameId: gameId,
            clientVersion: COLONIST_VERSION,
        }
        this.sendLobby(LobbyAction.ClickedSpectateGame, data)
    }

    static reconnectGame(gameId: string, analyticLabel: CA.Label) {
        void UIGameSpriteHandler.continueAfterAssetsLoad(() => {
            CA.sendGoogleAnalyticsEvent(CA.Category.Lobby, analyticLabel)
            const data: NetworkEnums.AccessGameLinkData = {
                gameId: gameId,
                clientVersion: COLONIST_VERSION,
            }
            this.sendLobby(LobbyAction.ReconnectGame, data)
        })
    }

    static autoRejoinGame() {
        this.sendLobby(LobbyAction.AutoRejoinGame, {})
    }

    static async accessGameFromLink(roomId: string) {
        this.sendAnalyticEvent(AnalyticEventType.JoinedRoom, AnalyticEventCategory.Room, roomId, JoinRoomMethod.EnteredRoomLink)
        try {
            await this.waitUntilGameCanBeStarted()
        } catch(error) {
            if(error instanceof AbortError) {
                this.updateURLToRoom('')
                return
            }
            throw error
        }

        const data: NetworkEnums.AccessGameLinkData = {
            gameId: roomId,
            clientVersion: COLONIST_VERSION,
        }
        this.sendLobby(LobbyAction.AccessGameLink, data)
    }

    private static startBeginnerMode(level: BeginnerModeLevel) {
        const data: NetworkEnums.StartBeginnerModeData = {
            clientVersion: COLONIST_VERSION,
            level: level,
            map: getMapForBeginnerModeLevel(level),
            bots: getBotsForBeginnerModeLevel(level),
        }
        this.sendLobby(LobbyAction.ClickedStartBeginnerMode, data)
    }

    private static start1v1BeginnerBotGame() {
        const data: NetworkEnums.StartBotGameData = {
            clientVersion: COLONIST_VERSION,
            bots: [
                BotDifficulty.BotEasy,
            ],
        }
        this.sendLobby(LobbyAction.Clicked1v1BeginnerBotGame, data)
    }

    private static updateURLToRoom(gameId: string) {
        if(window.location.hash == '#' + gameId) return
        window.location.assign('#' + gameId)
    }

    // ====================================================================================================================================
    // Channel
    // ====================================================================================================================================
    static joinChannel(channelId: string, serverProfile: DiscordServerProfile, isInitialLoad: boolean, roomOrGameId?: string) {
        void UIGameSpriteHandler.continueAfterAssetsLoad(() => {
            const data: ChannelJoinData = {
                channelId: channelId,
                isInitialLoad: isInitialLoad,
                roomOrGameId: roomOrGameId,
                ...serverProfile,
            }
            this.sendChannel(ChannelAction.ChannelJoin, data)
        }, () => {
            this.updateURLToRoom('')
        })
    }

    static clickedJoinChannelRoomOrGame(channelId: string, roomType: RoomType) {
        // Maybe add analytics here?
        void UIGameSpriteHandler.continueAfterAssetsLoad(() => {
            const data: ChannelJoinRoomOrGameData = {
                channelId: channelId,
                roomType: roomType,
            }
            this.sendChannel(ChannelAction.ChannelJoinRoomOrGame, data)
        })
    }

    static sendLobby(action: LobbyAction, payload: unknown) {
        const data: SendLobbyActionData = {
            action: action,
            payload: payload,
        }
        socket.controller.sender.sendToServerType(ClientMessageSubType.LobbyAction, 'lobby', data, PRIMARY_LOBBY_ACTIONS.includes(action))
    }

    static sendChannel(action: ChannelAction, payload: unknown) {
        const data: SendChannelActionData = {
            action: action,
            payload: payload,
        }
        socket.controller.sender.sendToServerType(ClientMessageSubType.ChannelAction, 'lobby', data, PRIMARY_CHANNEL_ACTIONS.includes(action))
    }

    static sendGeneral(action: GeneralAction, payload: unknown) {
        const data: SendGeneralActionData = {
            action: action,
            payload: payload,
        }
        socket.controller.sender.sendToServerType(ClientMessageSubType.GeneralAction, 'lobby', data, false)
    }

    private static sendAnalyticEvent(type: AnalyticEventType, category: AnalyticEventCategory, stringValue?: string, floatValue?: number) {
        const event: AjaxDataTypes.AnalyticData = {
            type: type,
            category: category,
            stringValue: stringValue,
            floatValue: floatValue,
        }
        void CA.sendAnalyticEvent(event)
    }

    private static async waitUntilGameCanBeStarted(): Promise<void> {
        await ClientUserState.requireUser()
        await UIGameSpriteHandler.continueAfterAssetsLoad()
    }
}
